import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { ASSETS_USER_DETAILS_METADATA, ASSETS_USER_DETAILS_TYPE, AssetsUser, AssetsUserDetails, FIREARM_ASSIGNMENT_TYPE, FIREARM_METADATA, FIREARM_STORAGE_LOCATION_METADATA, FIREARM_STORAGE_LOCATION_TYPE, FIREARM_TYPE, Firearm, FirearmAssignment, FirearmAssignmentStorageType, FirearmAssignmentType, FirearmStorageLocation, RANGER_METADATA, RANGER_TYPE, Ranger, RefCode, TRANSFER_ASSET_METADATA, TRANSFER_ASSET_TYPE, TransferAsset, setDefaultLocationFromUser } from 'assets-model';
import { AssetsUserDetailsService, FirearmAssignmentService, FirearmService, FirearmStorageLocationService, RangerService, RefCodeStorage, TransferAssetService } from 'assets-service';


@Directive()
export abstract class FirearmAssignmentItemComponentBase extends CrudItemComponent<FirearmAssignment, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  firearmSelectionList: Firearm[];

  gunDealerLocationSelectionList: FirearmStorageLocation[];

  locationSelectionList: FirearmStorageLocation[];

  rangerSelectionList: Ranger[];

  transferAssetSelectionList: TransferAsset[];

  userSelectionList: AssetsUserDetails[];

  firearmAssignmentTypeSelectionList: RefCode[];

  lifecycleStateSelectionList: RefCode[];

  nextActionSelectionList: RefCode[];

  storageTypeSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected firearmAssignmentService: FirearmAssignmentService,
    protected firearmService: FirearmService,
    protected gunDealerLocationService: FirearmStorageLocationService,
    protected locationService: FirearmStorageLocationService,
    protected rangerService: RangerService,
    protected transferAssetService: TransferAssetService,
    protected userService: AssetsUserDetailsService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(FIREARM_ASSIGNMENT_TYPE, fb, elementRef, router, route, userStorage, dialog, firearmAssignmentService, injector, snackbar);
  }

  createNewItem() {
    return new FirearmAssignment();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      firearmAssignmentId: [null, []],
      firearm: [null, []],
      gunDealerLocation: [null, []],
      location: [null, []],
      ranger: [null, []],
      transferAsset: [null, []],
      user: [null, []],
      actionPending: [null, []],
      activatedBy: [null, []],
      activatedDate: [null, []],
      buApprovalRejectionReason: [null, []],
      buApproved: [null, [requiredValidator,]],
      buApprovedRejectedBy: [null, []],
      buApprovedRejectedDate: [null, []],
      canceledBy: [null, []],
      canceledDate: [null, []],
      canceledReason: [null, []],
      comments: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      currentAssignment: [null, []],
      endDate: [null, []],
      firearmAssignmentType: [null, [requiredValidator,]],
      gunDealerLocationId: [null, []],
      handoverAcknowledgedBy: [null, []],
      handoverAcknowledgedDate: [null, []],
      lastStateChangeDate: [null, []],
      leadApprovalRejectionReason: [null, []],
      leadApproved: [null, [requiredValidator,]],
      leadApprovedRejectedBy: [null, []],
      leadApprovedRejectedDate: [null, []],
      lifecycleState: [null, []],
      locationId: [null, []],
      lockVersion: [null, []],
      nextAction: [null, []],
      notifyPolice: [null, [requiredValidator,]],
      personEmail: [null, [requiredValidator,]],
      personalFirearmAssignment: [null, [requiredValidator,]],
      policeNotificationEligible: [null, []],
      policeNotificationReason: [null, []],
      policeNotifiedDate: [null, []],
      previousStartedFirearmAssignmentId: [null, []],
      sortDate: [null, []],
      startDate: [null, []],
      startedBy: [null, []],
      storageType: [null, []],
      updatedBy: [null, []],
      updatedDate: [null, []],
      withExpiredStartToken: [null, []],
      withStartToken: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.firearmAssignmentId?.disable();
    }
    this.actionPending?.disable();
    this.currentAssignment?.disable();
    this.endDate?.disable();
    this.lastStateChangeDate?.disable();
    this.lifecycleState?.disable();
    this.nextAction?.disable();
    this.policeNotificationEligible?.disable();
    this.previousStartedFirearmAssignmentId?.disable();
    this.sortDate?.disable();
    this.withExpiredStartToken?.disable();
    this.withStartToken?.disable();
  }
  
  firearmAssignmentIdChanging(value: number, unsetDependents: boolean = true) {}
  
  firearmAssignmentIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.firearmAssignmentIdChangedByUser(value, unsetDependents);
    }
  }  
  
  firearmAssignmentIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  firearmDisplay(item: Firearm) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_TYPE.description(item);
  }
  
  firearmLoadSelectionList() {
    this.firearmService.findAll().subscribe(
      data => {
        this.firearmSelectionList = FIREARM_TYPE.entityDecorator.ensureContained(this.item && this.item.firearm, data.items);
      }
    );
  }

  firearmChanging(value: Firearm, unsetDependents: boolean = true) {}
  
  firearmChanged(value: Firearm, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.firearmChangedByUser(value, unsetDependents);
    }
  }  
  
  firearmChangedByUser(value: Firearm, unsetDependents: boolean = true) {
  }
  
  firearmCompare(a: any, b: any) {
    return FIREARM_METADATA.equals(a, b);
  }
  
  gunDealerLocationDisplay(item: FirearmStorageLocation) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_STORAGE_LOCATION_TYPE.description(item);
  }
  
  gunDealerLocationLoadSelectionList() {
    this.gunDealerLocationService.findAll().subscribe(
      data => {
        this.gunDealerLocationSelectionList = FIREARM_STORAGE_LOCATION_TYPE.entityDecorator.ensureContained(this.item && this.item.gunDealerLocation, data.items);
      }
    );
  }

  gunDealerLocationChanging(value: FirearmStorageLocation, unsetDependents: boolean = true) {}
  
  gunDealerLocationChanged(value: FirearmStorageLocation, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.gunDealerLocationChangedByUser(value, unsetDependents);
    }
  }  
  
  gunDealerLocationChangedByUser(value: FirearmStorageLocation, unsetDependents: boolean = true) {
  }
  
  gunDealerLocationCompare(a: any, b: any) {
    return FIREARM_STORAGE_LOCATION_METADATA.equals(a, b);
  }
  
  locationDisplay(item: FirearmStorageLocation) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_STORAGE_LOCATION_TYPE.description(item);
  }
  
  locationLoadSelectionList() {
    this.locationService.findAll().subscribe(
      data => {
        this.locationSelectionList = FIREARM_STORAGE_LOCATION_TYPE.entityDecorator.ensureContained(this.item && this.item.location, data.items);
      }
    );
  }

  locationChanging(value: FirearmStorageLocation, unsetDependents: boolean = true) {}
  
  locationChanged(value: FirearmStorageLocation, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.locationChangedByUser(value, unsetDependents);
    }
  }  
  
  locationChangedByUser(value: FirearmStorageLocation, unsetDependents: boolean = true) {
  }
  
  locationCompare(a: any, b: any) {
    return FIREARM_STORAGE_LOCATION_METADATA.equals(a, b);
  }
  
  rangerDisplay(item: Ranger) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return RANGER_TYPE.description(item);
  }
  
  rangerLoadSelectionList() {
    this.rangerService.findAll().subscribe(
      data => {
        this.rangerSelectionList = RANGER_TYPE.entityDecorator.ensureContained(this.item && this.item.ranger, data.items);
      }
    );
  }

  rangerChanging(value: Ranger, unsetDependents: boolean = true) {}
  
  rangerChanged(value: Ranger, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.rangerChangedByUser(value, unsetDependents);
    }
  }  
  
  rangerChangedByUser(value: Ranger, unsetDependents: boolean = true) {
  }
  
  rangerCompare(a: any, b: any) {
    return RANGER_METADATA.equals(a, b);
  }
  
  transferAssetDisplay(item: TransferAsset) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return TRANSFER_ASSET_TYPE.description(item);
  }
  
  transferAssetLoadSelectionList() {
    this.transferAssetService.findAll().subscribe(
      data => {
        this.transferAssetSelectionList = TRANSFER_ASSET_TYPE.entityDecorator.ensureContained(this.item && this.item.transferAsset, data.items);
      }
    );
  }

  transferAssetChanging(value: TransferAsset, unsetDependents: boolean = true) {}
  
  transferAssetChanged(value: TransferAsset, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.transferAssetChangedByUser(value, unsetDependents);
    }
  }  
  
  transferAssetChangedByUser(value: TransferAsset, unsetDependents: boolean = true) {
  }
  
  transferAssetCompare(a: any, b: any) {
    return TRANSFER_ASSET_METADATA.equals(a, b);
  }
  
  userDisplay(item: AssetsUserDetails) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSETS_USER_DETAILS_TYPE.description(item);
  }
  
  userLoadSelectionList() {
    this.userService.findAll().subscribe(
      data => {
        this.userSelectionList = ASSETS_USER_DETAILS_TYPE.entityDecorator.ensureContained(this.item && this.item.user, data.items);
      }
    );
  }

  userChanging(value: AssetsUserDetails, unsetDependents: boolean = true) {}
  
  userChanged(value: AssetsUserDetails, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.userChangedByUser(value, unsetDependents);
    }
  }  
  
  userChangedByUser(value: AssetsUserDetails, unsetDependents: boolean = true) {
  }
  
  userCompare(a: any, b: any) {
    return ASSETS_USER_DETAILS_METADATA.equals(a, b);
  }
  
  
  activatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  activatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.activatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  activatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  activatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  activatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.activatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  activatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  buApprovalRejectionReasonChanging(value: string, unsetDependents: boolean = true) {}
  
  buApprovalRejectionReasonChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.buApprovalRejectionReasonChangedByUser(value, unsetDependents);
    }
  }  
  
  buApprovalRejectionReasonChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  buApprovedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  buApprovedChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.buApprovedChangedByUser(value, unsetDependents);
    }
  }  
  
  buApprovedChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  buApprovedRejectedByChanging(value: string, unsetDependents: boolean = true) {}
  
  buApprovedRejectedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.buApprovedRejectedByChangedByUser(value, unsetDependents);
    }
  }  
  
  buApprovedRejectedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  buApprovedRejectedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  buApprovedRejectedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.buApprovedRejectedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  buApprovedRejectedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  canceledByChanging(value: string, unsetDependents: boolean = true) {}
  
  canceledByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.canceledByChangedByUser(value, unsetDependents);
    }
  }  
  
  canceledByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  canceledDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  canceledDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.canceledDateChangedByUser(value, unsetDependents);
    }
  }  
  
  canceledDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  canceledReasonChanging(value: string, unsetDependents: boolean = true) {}
  
  canceledReasonChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.canceledReasonChangedByUser(value, unsetDependents);
    }
  }  
  
  canceledReasonChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  commentsChanging(value: string, unsetDependents: boolean = true) {}
  
  commentsChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.commentsChangedByUser(value, unsetDependents);
    }
  }  
  
  commentsChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  
  
  firearmAssignmentTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_TYPE').subscribe(
      data => {
        this.firearmAssignmentTypeSelectionList = data;
      }
    );
  }

  firearmAssignmentTypeChanging(value: FirearmAssignmentType, unsetDependents: boolean = true) {}
  
  firearmAssignmentTypeChanged(value: FirearmAssignmentType, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.firearmAssignmentTypeChangedByUser(value, unsetDependents);
    }
  }  
  
  firearmAssignmentTypeChangedByUser(value: FirearmAssignmentType, unsetDependents: boolean = true) {
  }
  
  gunDealerLocationIdChanging(value: number, unsetDependents: boolean = true) {}
  
  gunDealerLocationIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.gunDealerLocationIdChangedByUser(value, unsetDependents);
    }
  }  
  
  gunDealerLocationIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  handoverAcknowledgedByChanging(value: string, unsetDependents: boolean = true) {}
  
  handoverAcknowledgedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.handoverAcknowledgedByChangedByUser(value, unsetDependents);
    }
  }  
  
  handoverAcknowledgedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  handoverAcknowledgedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  handoverAcknowledgedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.handoverAcknowledgedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  handoverAcknowledgedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  
  leadApprovalRejectionReasonChanging(value: string, unsetDependents: boolean = true) {}
  
  leadApprovalRejectionReasonChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.leadApprovalRejectionReasonChangedByUser(value, unsetDependents);
    }
  }  
  
  leadApprovalRejectionReasonChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  leadApprovedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  leadApprovedChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.leadApprovedChangedByUser(value, unsetDependents);
    }
  }  
  
  leadApprovedChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  leadApprovedRejectedByChanging(value: string, unsetDependents: boolean = true) {}
  
  leadApprovedRejectedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.leadApprovedRejectedByChangedByUser(value, unsetDependents);
    }
  }  
  
  leadApprovedRejectedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  leadApprovedRejectedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  leadApprovedRejectedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.leadApprovedRejectedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  leadApprovedRejectedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  lifecycleStateLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_LIFECYCLE_STATE').subscribe(
      data => {
        this.lifecycleStateSelectionList = data;
      }
    );
  }

  
  locationIdChanging(value: number, unsetDependents: boolean = true) {}
  
  locationIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.locationIdChangedByUser(value, unsetDependents);
    }
  }  
  
  locationIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  nextActionLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_NEXT_ACTION').subscribe(
      data => {
        this.nextActionSelectionList = data;
      }
    );
  }

  
  notifyPoliceChanging(value: boolean, unsetDependents: boolean = true) {}
  
  notifyPoliceChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.notifyPoliceChangedByUser(value, unsetDependents);
    }
  }  
  
  notifyPoliceChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  personEmailChanging(value: string, unsetDependents: boolean = true) {}
  
  personEmailChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.personEmailChangedByUser(value, unsetDependents);
    }
  }  
  
  personEmailChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  personalFirearmAssignmentChanging(value: boolean, unsetDependents: boolean = true) {}
  
  personalFirearmAssignmentChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.personalFirearmAssignmentChangedByUser(value, unsetDependents);
    }
  }  
  
  personalFirearmAssignmentChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  
  policeNotificationReasonChanging(value: string, unsetDependents: boolean = true) {}
  
  policeNotificationReasonChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.policeNotificationReasonChangedByUser(value, unsetDependents);
    }
  }  
  
  policeNotificationReasonChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  policeNotifiedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  policeNotifiedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.policeNotifiedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  policeNotifiedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  
  
  startDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  startDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.startDateChangedByUser(value, unsetDependents);
    }
  }  
  
  startDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  startedByChanging(value: string, unsetDependents: boolean = true) {}
  
  startedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.startedByChangedByUser(value, unsetDependents);
    }
  }  
  
  startedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  storageTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_STORAGE_TYPE').subscribe(
      data => {
        this.storageTypeSelectionList = data;
      }
    );
  }

  storageTypeChanging(value: FirearmAssignmentStorageType, unsetDependents: boolean = true) {}
  
  storageTypeChanged(value: FirearmAssignmentStorageType, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.storageTypeChangedByUser(value, unsetDependents);
    }
  }  
  
  storageTypeChangedByUser(value: FirearmAssignmentStorageType, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get firearmAssignmentId() {
    return this.control('firearmAssignmentId');
  }
  
  get firearm() {
    return this.control('firearm');
  }
  
  get gunDealerLocation() {
    return this.control('gunDealerLocation');
  }
  
  get location() {
    return this.control('location');
  }
  
  get ranger() {
    return this.control('ranger');
  }
  
  get transferAsset() {
    return this.control('transferAsset');
  }
  
  get user() {
    return this.control('user');
  }
  
  get actionPending() {
    return this.control('actionPending');
  }
  
  get activatedBy() {
    return this.control('activatedBy');
  }
  
  get activatedDate() {
    return this.control('activatedDate');
  }
  
  get buApprovalRejectionReason() {
    return this.control('buApprovalRejectionReason');
  }
  
  get buApproved() {
    return this.control('buApproved');
  }
  
  get buApprovedRejectedBy() {
    return this.control('buApprovedRejectedBy');
  }
  
  get buApprovedRejectedDate() {
    return this.control('buApprovedRejectedDate');
  }
  
  get canceledBy() {
    return this.control('canceledBy');
  }
  
  get canceledDate() {
    return this.control('canceledDate');
  }
  
  get canceledReason() {
    return this.control('canceledReason');
  }
  
  get comments() {
    return this.control('comments');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get currentAssignment() {
    return this.control('currentAssignment');
  }
  
  get endDate() {
    return this.control('endDate');
  }
  
  get firearmAssignmentType() {
    return this.control('firearmAssignmentType');
  }
  
  get gunDealerLocationId() {
    return this.control('gunDealerLocationId');
  }
  
  get handoverAcknowledgedBy() {
    return this.control('handoverAcknowledgedBy');
  }
  
  get handoverAcknowledgedDate() {
    return this.control('handoverAcknowledgedDate');
  }
  
  get lastStateChangeDate() {
    return this.control('lastStateChangeDate');
  }
  
  get leadApprovalRejectionReason() {
    return this.control('leadApprovalRejectionReason');
  }
  
  get leadApproved() {
    return this.control('leadApproved');
  }
  
  get leadApprovedRejectedBy() {
    return this.control('leadApprovedRejectedBy');
  }
  
  get leadApprovedRejectedDate() {
    return this.control('leadApprovedRejectedDate');
  }
  
  get lifecycleState() {
    return this.control('lifecycleState');
  }
  
  get locationId() {
    return this.control('locationId');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get nextAction() {
    return this.control('nextAction');
  }
  
  get notifyPolice() {
    return this.control('notifyPolice');
  }
  
  get personEmail() {
    return this.control('personEmail');
  }
  
  get personalFirearmAssignment() {
    return this.control('personalFirearmAssignment');
  }
  
  get policeNotificationEligible() {
    return this.control('policeNotificationEligible');
  }
  
  get policeNotificationReason() {
    return this.control('policeNotificationReason');
  }
  
  get policeNotifiedDate() {
    return this.control('policeNotifiedDate');
  }
  
  get previousStartedFirearmAssignmentId() {
    return this.control('previousStartedFirearmAssignmentId');
  }
  
  get sortDate() {
    return this.control('sortDate');
  }
  
  get startDate() {
    return this.control('startDate');
  }
  
  get startedBy() {
    return this.control('startedBy');
  }
  
  get storageType() {
    return this.control('storageType');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
  get withExpiredStartToken() {
    return this.control('withExpiredStartToken');
  }
  
  get withStartToken() {
    return this.control('withStartToken');
  }
  
}





